/**
 * EmailResendConfig - Configurações de e-mail→ticket do provider `email-resend`
 * (Plano B — Email→Ticket, Task 7).
 *
 * Usado dentro da tela de edição de canal (EditChannelPage). Reúne:
 * - Modo de criação de ticket (off/review/auto) + categoria/prioridade/equipe
 * - Resposta automática (com validação de mensagem obrigatória)
 * - Identidade do remetente (nome de exibição + assinatura)
 *
 * Estado inicial usa `channel.config` (já resolvido pelo backend em
 * `findById`) para evitar flash de loading, e o GET dedicado
 * (`useChannelEmailConfig`) SEMEIA o form uma única vez quando resolve —
 * refetches nunca atropelam edição em curso (ver useEffect de sync). Salva
 * via PATCH com dirty-tracking — só os campos alterados são enviados — e
 * re-sincroniza o baseline a partir da response do PATCH.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import type { ChannelResponse } from '@/modules/channels/types';
import type {
  ChannelEmailConfigResponse,
  EmailTicketPriority,
  UpdateChannelEmailConfigRequest,
} from '@/modules/channels/types';
import {
  FormSection,
  Input,
  RadioGroup,
  Select,
  Switch,
  Textarea,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useChannelEmailConfig, useUpdateChannelEmailConfig } from '../../hooks';
import {
  EMAIL_TICKET_CATEGORY_OPTIONS,
  EMAIL_TICKET_PRIORITY_OPTIONS,
  TICKET_CREATION_MODE_OPTIONS,
} from '../../constants/emailTicketOptions';
import { RateLimitsCard } from './RateLimitsCard';

interface EmailResendConfigProps {
  channel: ChannelResponse;
  /** Reporta dirty ao "Salvar Alterações" único da página (EditChannelPage). */
  onDirtyChange?: (dirty: boolean) => void;
  /** A página registra aqui a função de save deste bloco — retorna false em
   *  falha de validação/rede para a página abortar o feedback de sucesso. */
  saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
}

type TicketCreationMode = 'off' | 'review' | 'auto';

interface EmailConfigFormState {
  ticketCreationMode: TicketCreationMode;
  ticketCategory: string;
  ticketPriority: EmailTicketPriority | '';
  ticketTeamId: string;
  autoReplyEnabled: boolean;
  autoReplyMessage: string;
  displayName: string;
  signature: string;
}

const readFormState = (source: Partial<ChannelEmailConfigResponse> | undefined): EmailConfigFormState => ({
  ticketCreationMode: source?.ticketCreationMode ?? 'off',
  ticketCategory: source?.ticketCategory ?? '',
  ticketPriority: source?.ticketPriority ?? '',
  ticketTeamId: source?.ticketTeamId ?? '',
  autoReplyEnabled: source?.autoReplyEnabled ?? false,
  autoReplyMessage: source?.autoReplyMessage ?? '',
  displayName: source?.displayName ?? '',
  signature: source?.signature ?? '',
});

export const EmailResendConfig: React.FC<EmailResendConfigProps> = ({ channel, onDirtyChange, saveRef }) => {
  // `ChannelResponse.config` já surfaceia o mesmo subset resolvido (via
  // repository.findById) — usado como estado inicial instantâneo enquanto o
  // GET dedicado carrega, evitando flash de campos vazios.
  const channelConfig = channel.config as Partial<ChannelEmailConfigResponse> | undefined;

  const { data, isLoading } = useChannelEmailConfig(channel.id);
  const updateMutation = useUpdateChannelEmailConfig(channel.id);
  const { data: teams = [] } = useTeamsDropdown();

  const [initial, setInitial] = useState<EmailConfigFormState>(() => readFormState(channelConfig));
  const [form, setForm] = useState<EmailConfigFormState>(() => readFormState(channelConfig));
  const [autoReplyError, setAutoReplyError] = useState<string | null>(null);

  // Sincroniza com a fonte dedicada (GET /channels/:id/email-config).
  // Regras (fix pós-auditoria 2026-07-13):
  //  (a) o form é SEMEADO uma única vez, quando o primeiro `data` resolve
  //      (sobrepõe o estado otimista vindo de `channel.config`);
  //  (b) refetches subsequentes (refetchOnWindowFocus, invalidateQueries)
  //      NUNCA atropelam edição em curso — se `isDirty`, o snapshot do
  //      usuário vence; sem edição pendente, re-sincroniza baseline+form.
  // Sem essas guardas, qualquer resolução do GET reescrevia o form e apagava
  // silenciosamente a escolha do usuário (isDirty=false → save virava no-op).
  const initializedRef = useRef(false);
  const isDirtyRef = useRef(false);
  useEffect(() => {
    if (!data) return;
    const next = readFormState(data);
    if (!initializedRef.current) {
      initializedRef.current = true;
      setInitial(next);
      // Preserva edição feita durante a janela de load do GET dedicado.
      setForm((prev) => (isDirtyRef.current ? prev : next));
      return;
    }
    if (isDirtyRef.current) return;
    setInitial(next);
    setForm(next);
  }, [data]);

  const emailAddress = data?.emailAddress ?? channelConfig?.emailAddress;

  const handleFieldChange = <K extends keyof EmailConfigFormState>(key: K, value: EmailConfigFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'autoReplyEnabled' || key === 'autoReplyMessage') {
      setAutoReplyError(null);
    }
  };

  const isDirty = useMemo(
    () => (Object.keys(form) as Array<keyof EmailConfigFormState>).some((key) => form[key] !== initial[key]),
    [form, initial]
  );
  // Espelho em ref pro useEffect de sync ler o valor do render corrente sem
  // entrar nas deps (evita re-execução a cada tecla e loop de setState).
  isDirtyRef.current = isDirty;

  // Integração com o "Salvar Alterações" único da página: reporta dirty
  // (habilita o botão do header) e zera ao desmontar.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const teamOptions = useMemo(
    () => [
      { value: '', label: 'Sem equipe (distribuição automática)' },
      ...teams.map((team) => ({ value: team.id, label: team.name })),
    ],
    [teams]
  );

  const handleSave = async (): Promise<boolean> => {
    if (form.autoReplyEnabled && form.autoReplyMessage.trim().length === 0) {
      setAutoReplyError('Informe a mensagem de resposta automática.');
      toast.error('Informe a mensagem de resposta automática antes de salvar.');
      return false;
    }
    setAutoReplyError(null);

    const patch: UpdateChannelEmailConfigRequest = {};
    if (form.ticketCreationMode !== initial.ticketCreationMode) {
      patch.ticketCreationMode = form.ticketCreationMode;
    }
    if (form.ticketCategory !== initial.ticketCategory) {
      patch.ticketCategory = form.ticketCategory;
    }
    if (form.ticketPriority !== initial.ticketPriority && form.ticketPriority !== '') {
      patch.ticketPriority = form.ticketPriority;
    }
    if (form.ticketTeamId !== initial.ticketTeamId) {
      patch.ticketTeamId = form.ticketTeamId ? form.ticketTeamId : null;
    }
    if (form.autoReplyEnabled !== initial.autoReplyEnabled) {
      patch.autoReplyEnabled = form.autoReplyEnabled;
    }
    if (form.autoReplyMessage !== initial.autoReplyMessage) {
      patch.autoReplyMessage = form.autoReplyMessage.trim();
    }
    if (form.displayName !== initial.displayName) {
      patch.displayName = form.displayName.trim();
    }
    if (form.signature !== initial.signature) {
      patch.signature = form.signature.trim();
    }

    if (Object.keys(patch).length === 0) {
      // isDirty=true sem nenhum campo virar patch (ex: ticketPriority limpo
      // para '' — nunca enviado). Nada a persistir: sucesso silencioso, o
      // save unificado da página segue o fluxo normal.
      setInitial(form);
      return true;
    }

    try {
      // O PATCH devolve a config atualizada (controller responde
      // {success, data: EmailChannelConfigView}; channelsApi desempacota
      // response.data.data) — re-sincroniza baseline+form pra zerar o
      // dirty-tracking com a verdade do servidor, sem esperar o refetch.
      const updated = await updateMutation.mutateAsync(patch);
      const next = readFormState(updated);
      setInitial(next);
      setForm(next);
      return true;
    } catch (error: unknown) {
      const backendMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      toast.error(backendMessage || (error instanceof Error ? error.message : 'Erro ao atualizar configurações de e-mail.'));
      return false;
    }
  };

  // Registra a função de save no ref da página (o único botão "Salvar
  // Alterações" do header chama daqui). Atribuição por render mantém o
  // closure atual de `form`/`initial` — mesmo padrão do isDirtyRef.
  if (saveRef) {
    saveRef.current = handleSave;
  }

  if (isLoading && !channelConfig) {
    return (
      <FormSection icon="inbox" title="Criação de tickets">
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Carregando configurações...
        </p>
      </FormSection>
    );
  }

  return (
    <div className="space-y-4" data-testid="channel-email-config">
      <FormSection
        icon="inbox"
        title="Criação de tickets"
        description="Defina o que acontece quando um e-mail chega neste canal."
      >
        <div data-testid="channel-email-creation-mode">
          <RadioGroup
            name="channel-email-ticket-creation-mode"
            options={TICKET_CREATION_MODE_OPTIONS}
            value={form.ticketCreationMode}
            onChange={(value) => handleFieldChange('ticketCreationMode', value as TicketCreationMode)}
            variant="cards"
          />
        </div>

        {form.ticketCreationMode !== 'off' && (
          <>
            <div
              className="flex items-start gap-2 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--theme-primary-background)' }}
              data-testid="channel-email-creation-hint"
            >
              <Icon name="info" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Com criação ativa, e-mails recebidos viram tickets e não aparecem no chat.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select
                label="Categoria"
                value={form.ticketCategory}
                onChange={(value) => handleFieldChange('ticketCategory', value)}
                options={EMAIL_TICKET_CATEGORY_OPTIONS}
                data-testid="channel-email-ticket-category"
              />
              <Select
                label="Prioridade"
                value={form.ticketPriority}
                onChange={(value) => handleFieldChange('ticketPriority', value as EmailTicketPriority)}
                options={EMAIL_TICKET_PRIORITY_OPTIONS}
                placeholder="Selecione"
                data-testid="channel-email-ticket-priority"
              />
              <Select
                label="Equipe"
                value={form.ticketTeamId}
                onChange={(value) => handleFieldChange('ticketTeamId', value)}
                options={teamOptions}
                data-testid="channel-email-ticket-team"
              />
            </div>
          </>
        )}
      </FormSection>

      <FormSection
        icon="send"
        title="Resposta automática"
        description="Envia uma resposta automática para todo e-mail recebido neste canal."
      >
        <div data-testid="channel-email-auto-reply-toggle">
          <Switch
            label="Ativar resposta automática"
            checked={form.autoReplyEnabled}
            onChange={(checked) => handleFieldChange('autoReplyEnabled', checked)}
          />
        </div>

        {form.autoReplyEnabled && (
          <Textarea
            label="Mensagem de Resposta Automática"
            required
            value={form.autoReplyMessage}
            onChange={(e) => handleFieldChange('autoReplyMessage', e.target.value)}
            placeholder="Olá! Recebemos seu e-mail e entraremos em contato em breve."
            rows={3}
            error={autoReplyError ?? undefined}
            data-testid="channel-email-auto-reply-message"
          />
        )}
      </FormSection>

      <FormSection
        icon="mail"
        title="Remetente"
        description="Identidade usada no envio de e-mails deste canal."
      >
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
            Endereço de e-mail
          </label>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }} data-testid="channel-email-address">
            {emailAddress || '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
            Nome de Exibição
          </label>
          <Input
            type="text"
            value={form.displayName}
            onChange={(e) => handleFieldChange('displayName', e.target.value)}
            placeholder="Equipe de Suporte"
            data-testid="channel-email-display-name"
          />
        </div>

        <Textarea
          label="Assinatura (HTML)"
          description="Opcional — assinatura HTML adicionada ao final de cada e-mail."
          value={form.signature}
          onChange={(e) => handleFieldChange('signature', e.target.value)}
          placeholder="<p>Atenciosamente,<br/>Equipe de Suporte</p>"
          rows={3}
          data-testid="channel-email-signature"
        />
      </FormSection>

      <RateLimitsCard channelId={channel.id} />
    </div>
  );
};

EmailResendConfig.displayName = 'EmailResendConfig';
